import { notification, Spin } from "antd";
import classNames from "classnames";
import { get } from "lodash";
import { useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiCheck, BiChevronLeft } from "react-icons/bi";
import {
  MdClose,
  MdErrorOutline,
  MdOutlineCategory,
  MdOutlineCheckCircle,
  MdOutlineCloudUpload,
  MdOutlineDownload,
  MdOutlineError,
  MdOutlineExplore,
  MdOutlineInsertDriveFile,
  MdOutlineRefresh,
  MdOutlineTableRows,
  MdOutlineWarning,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Button from "shared/components/Button";
import Header from "shared/components/Header";
import Select from "shared/components/Select";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import {
  VALIDATE_INSURANCE_PLAN_PARAMS_IMPORT,
  EXECUTE_INSURANCE_PLAN_PARAMS_IMPORT,
} from "utils/api/graphql/mutations/insurance-plans";
import { INSURANCE_PLANS } from "utils/api/graphql/queries/insurance-plans";
import { GET_IDCC_LIST } from "utils/api/graphql/queries/idcc";
import { PROCESSES } from "utils/api/graphql/queries/processes";
import {
  CONTACT_EXERCISE_FRAME,
  DYNAMIC_DOCUMENTS,
  GENERATED_DOCUMENTS_VERSIONS,
  INITIAL_VALUES,
  JOB_LIST,
  LMF_ASSOCIATION_MEMBER_TYPE,
  PROFILE_LIST,
  SCREENS,
  SOCIAL_REGIMES,
} from "utils/constants";
import * as XLSX from "xlsx";
import { autoFitColumns } from "../constants";
import { REFERENTIAL_TYPES } from "../constants";
import StepCard from "../StepCard";
import StepValueMapping from "../StepValueMapping";
import {
  ACCEPTED_FILE_EXTENSIONS,
  CSV_TEMPLATE_HEADERS,
  FILE_ACCEPT_STRING,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  PARAMS_IMPORT_STEPS,
  resolveError,
} from "./constants";
import { StoreContext } from "shared/store";

const STEPS = [
  PARAMS_IMPORT_STEPS.UPLOAD,
  PARAMS_IMPORT_STEPS.MAPPING,
  PARAMS_IMPORT_STEPS.VALIDATION,
  PARAMS_IMPORT_STEPS.VALUE_MAPPING,
  PARAMS_IMPORT_STEPS.ANALYSIS,
  PARAMS_IMPORT_STEPS.IMPORTING,
  PARAMS_IMPORT_STEPS.RESULT,
];

const ParamsImportWizard = () => {
  const { user } = useContext(StoreContext);
  const template = get(user, `organization.config.envelop`);
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const PARAMS_IMPORT = t("PARAMS_IMPORT", { returnObjects: true });
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [fileData, setFileData] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importResult, setImportResult] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [valueMappingState, setValueMappingState] = useState({
    maps: {},
    save: {},
    refNames: {},
    selectedRefIds: {},
  });
  const [valueMappings, setValueMappings] = useState(null);

  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [sizeError, setSizeError] = useState(false);
  const inputRef = useRef(null);

  const { data: processData } = useQuery(PROCESSES, {
    variables: { take: 500 },
  });

  // IDCC référentiel is not backed by the API yet (no getIdccList query),
  // so skip the request to avoid a GraphQL validation error.
  const { data: idccListData } = useQuery(GET_IDCC_LIST, {
    variables: {
      take: null,
      skip: 0,
      orderBy: [{ code: "ASC" }],
    },
    skip: true,
  });

  const [validateImport, { loading: validating }] = useMutation(
    VALIDATE_INSURANCE_PLAN_PARAMS_IMPORT,
  );

  const [executeImport] = useMutation(EXECUTE_INSURANCE_PLAN_PARAMS_IMPORT, {
    refetchQueries: [
      {
        query: INSURANCE_PLANS,
        awaitRefetchQueries: true,
        variables: INITIAL_VALUES,
      },
    ],
  });

  const stepName = STEPS[currentStep];

  const stepLabels = STEPS.map((step) => {
    const labelMap = {
      [PARAMS_IMPORT_STEPS.UPLOAD]: PARAMS_IMPORT.STEPS.UPLOAD,
      [PARAMS_IMPORT_STEPS.MAPPING]: PARAMS_IMPORT.STEPS.MAPPING,
      [PARAMS_IMPORT_STEPS.VALIDATION]: PARAMS_IMPORT.STEPS.VALIDATION,
      [PARAMS_IMPORT_STEPS.VALUE_MAPPING]:
        PARAMS_IMPORT.STEPS?.VALUE_MAPPING || "Values",
      [PARAMS_IMPORT_STEPS.ANALYSIS]: PARAMS_IMPORT.STEPS.ANALYSIS,
      [PARAMS_IMPORT_STEPS.IMPORTING]: PARAMS_IMPORT.STEPS.IMPORTING,
      [PARAMS_IMPORT_STEPS.RESULT]: PARAMS_IMPORT.STEPS.RESULT,
    };
    return labelMap[step] || step;
  });

  const visibleStepIndex = STEPS.indexOf(stepName);

  const downloadTemplate = useCallback(() => {
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([CSV_TEMPLATE_HEADERS]);
    autoFitColumns(worksheet, CSV_TEMPLATE_HEADERS);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Paramètres");
    XLSX.writeFile(workbook, "insurance-plans-params-template.xlsx");
  }, []);

  const handleFile = (file) => {
    setSizeError(false);
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!ACCEPTED_FILE_EXTENSIONS.includes(ext)) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setSizeError(true);
      return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      handleFileUpload(reader.result, file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleFileUpload = async (base64, filename) => {
    setFileData({ base64, filename });
    try {
      const { data } = await validateImport({
        variables: { data: { base64, filename } },
      });
      const result = get(data, "validateInsurancePlanParamsImport");
      setValidationResult(result);
      setMapping(result.autoMapping || result.mapping || {});
      setCurrentStep(STEPS.indexOf(PARAMS_IMPORT_STEPS.MAPPING));
    } catch (error) {
      notification.error({
        message: PARAMS_IMPORT.ERRORS.UPLOAD_FAILED,
        description: error.message,
      });
    }
  };

  const handleMappingConfirm = async (updatedMapping) => {
    setMapping(updatedMapping);
    try {
      const { data } = await validateImport({
        variables: {
          data: {
            base64: fileData.base64,
            filename: fileData.filename,
            mapping: updatedMapping,
          },
        },
      });
      const result = get(data, "validateInsurancePlanParamsImport");
      setValidationResult(result);
      setCurrentStep(STEPS.indexOf(PARAMS_IMPORT_STEPS.VALIDATION));
    } catch (error) {
      notification.error({
        message: PARAMS_IMPORT.ERRORS.VALIDATION_FAILED,
        description: error.message,
      });
    }
  };

  const handleValidationNext = () => {
    const distinctValues = get(validationResult, "preview.distinctValues", {});
    const hasValues = Object.values(distinctValues).some(
      (arr) => arr && arr.length > 0,
    );
    if (hasValues) {
      setCurrentStep(STEPS.indexOf(PARAMS_IMPORT_STEPS.VALUE_MAPPING));
    } else {
      setCurrentStep(STEPS.indexOf(PARAMS_IMPORT_STEPS.ANALYSIS));
    }
  };

  const handleValueMappingConfirm = (mappings) => {
    setValueMappings(mappings);
    setCurrentStep(STEPS.indexOf(PARAMS_IMPORT_STEPS.ANALYSIS));
  };

  const buildParamsSections = () => {
    const distinctValues = get(validationResult, "preview.distinctValues", {});
    const VM = PARAMS_IMPORT.VALUE_MAPPING || {};
    const optionTranslations = get(VM, "OPTIONS", {});
    const sections = [];
    const DETAIL = t("DETAIL", { returnObjects: true });
    const DOCUMENT_NAME = t("DOCUMENT_NAME", { returnObjects: true });
    const docTemplates = get(DETAIL, "DOCUMENTS", {});

    const toOptions = (obj, translationKey) => {
      const labels = optionTranslations[translationKey] || {};
      return Object.entries(obj).map(([key, value]) => ({
        label: labels[value] || key,
        value,
      }));
    };

    const buildDocVersionOptions = (docTypeKey) => {
      const configuredTemplates = DYNAMIC_DOCUMENTS[template][docTypeKey];
      const versions = configuredTemplates
        ? Object.keys(configuredTemplates)
        : GENERATED_DOCUMENTS_VERSIONS[docTypeKey] || [];
      const templates = get(docTemplates, `${docTypeKey}.TEMPLATES`, {});
      return versions.map((v) => ({
        label:
          get(DOCUMENT_NAME, `${template}.${docTypeKey}.${v}`) ||
          templates[v] ||
          v,
        value: v,
      }));
    };

    const sectionKeys = get(VM, "SECTIONS", {});

    const fieldConfigs = [
      {
        key: "SOCIAL_REGIMES",
        title: sectionKeys.SOCIAL_REGIMES || "Social regimes",
        options: toOptions(SOCIAL_REGIMES, "SOCIAL_REGIMES"),
        referentialType: REFERENTIAL_TYPES.SOCIAL_REGIMES,
      },
      {
        key: "EXERCISE_FRAME",
        title: sectionKeys.EXERCISE_FRAME || "Exercise frame",
        options: toOptions(CONTACT_EXERCISE_FRAME, "EXERCISE_FRAME"),
        referentialType: REFERENTIAL_TYPES.EXERCISE_FRAME,
      },
      {
        key: "PROFESSION",
        title: sectionKeys.PROFESSION || "Profession",
        options: toOptions(JOB_LIST, "PROFESSION"),
        referentialType: REFERENTIAL_TYPES.PROFESSION,
      },
      {
        key: "PROFILES",
        title: sectionKeys.PROFILES || "Profiles",
        options: toOptions(PROFILE_LIST, "PROFILES"),
        referentialType: REFERENTIAL_TYPES.PROFILES,
      },
      {
        key: "SUB_PROFILES",
        title: sectionKeys.SUB_PROFILES || "Sub-profiles",
        options: toOptions(LMF_ASSOCIATION_MEMBER_TYPE, "SUB_PROFILES"),
        referentialType: REFERENTIAL_TYPES.SUB_PROFILES,
      },
      {
        key: "IDCC",
        title: sectionKeys.IDCC || "IDCC",
        options: (get(idccListData, "getIdccList.data") || []).map((row) => ({
          label: `${row.code} — ${row.label}`,
          value: row.code,
        })),
      },
      {
        key: "PROCESSES",
        title: sectionKeys.PROCESSES || "Processes",
        options: (get(processData, "processes.data") || []).map((p) => ({
          label: p.name,
          value: p.id,
        })),
        referentialType: REFERENTIAL_TYPES.PROCESSES,
      },
      {
        key: "DOC_QUOTE",
        title: sectionKeys.DOC_QUOTE || "Devis",
        options: buildDocVersionOptions("QUOTE"),
      },
      {
        key: "DOC_DDA",
        title: sectionKeys.DOC_DDA || "Devoir de conseil (DDA)",
        options: buildDocVersionOptions("DUTY_OF_ADVICE"),
      },
      {
        key: "DOC_MEMBERSHIP_FORM",
        title: sectionKeys.DOC_MEMBERSHIP_FORM || "Bulletin d'adhésion",
        options: buildDocVersionOptions("MEMBERSHIP_FORM"),
      },
      {
        key: "DOC_SEPA",
        title: sectionKeys.DOC_SEPA || "Mandat SEPA",
        options: buildDocVersionOptions("SEPA_DIRECT_DEBIT_MANDATE"),
      },
      {
        key: "DOC_TERMINATION_LETTER",
        title: sectionKeys.DOC_TERMINATION_LETTER || "Lettre de résiliation",
        options: buildDocVersionOptions("TERMINATION_LETTER"),
      },
      {
        key: "DOC_TERMINATION_MANDATE",
        title: sectionKeys.DOC_TERMINATION_MANDATE || "Mandat de résiliation",
        options: buildDocVersionOptions("TERMINATION_MANDATE"),
      },
    ];

    for (const config of fieldConfigs) {
      const fileValues = distinctValues[config.key] || [];
      if (fileValues.length > 0) {
        sections.push({
          ...config,
          fileValues,
        });
      }
    }

    return sections;
  };

  const handleExecuteImport = async () => {
    setCurrentStep(STEPS.indexOf(PARAMS_IMPORT_STEPS.IMPORTING));
    try {
      const { data } = await executeImport({
        variables: {
          data: {
            base64: fileData.base64,
            filename: fileData.filename,
            mapping,
            strategy: "all",
            ...(valueMappings ? { valueMappings } : {}),
          },
        },
      });
      const result = get(data, "executeInsurancePlanParamsImport");
      if (result) {
        setImportResult(result);
      } else {
        setImportResult({
          success: false,
          errors: [{ key: "UNEXPECTED_ERROR", params: {} }],
        });
      }
      setCurrentStep(STEPS.indexOf(PARAMS_IMPORT_STEPS.RESULT));
    } catch (error) {
      const gqlMsg = get(error, "graphQLErrors[0].message", "");
      const msg = gqlMsg || error.message || "";
      let parsedError;
      try {
        parsedError = JSON.parse(msg);
      } catch (_e) {
        parsedError = null;
      }
      const errorObj =
        parsedError && parsedError.key
          ? parsedError
          : { key: "UNEXPECTED_ERROR", params: { message: msg } };
      setImportResult({
        success: false,
        errors: [errorObj],
      });
      setCurrentStep(STEPS.indexOf(PARAMS_IMPORT_STEPS.RESULT));
    }
  };

  const handleFinish = () => {
    navigate(SCREENS.PRIVATE.ADMIN.INSURANCE_PLANS.LIST.path);
  };

  const handleReset = () => {
    setCurrentStep(0);
    setFileData(null);
    setValidationResult(null);
    setMapping({});
    setImportResult(null);
    setConfirmed(false);
    setSelectedFile(null);
    setSizeError(false);
    setDragOver(false);
    setValueMappingState({
      maps: {},
      save: {},
      refNames: {},
      selectedRefIds: {},
    });
    setValueMappings(null);
  };

  const goBack = () => {
    if (currentStep === 0) {
      navigate(SCREENS.PRIVATE.ADMIN.INSURANCE_PLANS.LIST.path);
      return;
    }
    const currentVisible = STEPS.indexOf(stepName);
    if (currentVisible > 0) {
      const prevStep = STEPS[currentVisible - 1];
      setCurrentStep(STEPS.indexOf(prevStep));
    }
  };

  const renderUpload = () => {
    const handleDrop = (e) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    };

    return (
      <StepCard
        title={PARAMS_IMPORT.UPLOAD.TITLE}
        description={PARAMS_IMPORT.UPLOAD.DESCRIPTION}
      >
        <div
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onClick={() => inputRef.current && inputRef.current.click()}
          className={classNames("pricers-import__dropzone", {
            "pricers-import__dropzone--dragover": dragOver && !selectedFile,
            "pricers-import__dropzone--selected": !!selectedFile,
          })}
        >
          {validating ? (
            <div>
              <Spin size="large" />
              <p className="pricers-import__dropzone-loading">
                {PARAMS_IMPORT.UPLOAD.ANALYZING}
              </p>
            </div>
          ) : selectedFile ? (
            <div>
              <MdOutlineInsertDriveFile size={48} color={COLORS.C_SUCCESS} />
              <p className="pricers-import__dropzone-filename">
                {selectedFile.name}
              </p>
              <p className="pricers-import__dropzone-filesize">
                {(selectedFile.size / 1024).toFixed(1)} KB
              </p>
            </div>
          ) : (
            <div>
              <MdOutlineCloudUpload size={48} color={COLORS.C_GRAY} />
              <p className="pricers-import__dropzone-text">
                {PARAMS_IMPORT.UPLOAD.DROP_TEXT}
              </p>
              <p className="pricers-import__dropzone-hint">
                {PARAMS_IMPORT.UPLOAD.FORMATS}
              </p>
              <p className="pricers-import__dropzone-hint">
                {PARAMS_IMPORT.UPLOAD.MAX_SIZE.replace(
                  "{{size}}",
                  MAX_FILE_SIZE_MB,
                )}
              </p>
            </div>
          )}
        </div>
        {sizeError && (
          <div className="pricers-import__upload-error">
            <MdOutlineError size={18} />
            {PARAMS_IMPORT.UPLOAD.FILE_TOO_LARGE.replace(
              "{{size}}",
              MAX_FILE_SIZE_MB,
            )}
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={FILE_ACCEPT_STRING}
          className="hidden-input"
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) handleFile(file);
          }}
        />
        <div className="pricers-import__template-section">
          <div className="pricers-import__template-hint">
            <p>{PARAMS_IMPORT.UPLOAD.TEMPLATE_HINT}</p>
            <p>{PARAMS_IMPORT.UPLOAD.REQUIRED_HINT}</p>
          </div>
          <button
            type="button"
            className="pricers-import__template-btn"
            onClick={downloadTemplate}
          >
            <MdOutlineDownload size={18} />
            {PARAMS_IMPORT.UPLOAD.DOWNLOAD_TEMPLATE}
          </button>
        </div>
      </StepCard>
    );
  };

  const renderMapping = () => {
    const fields = get(validationResult, "fields", []);
    const headers = get(validationResult, "headers", []);
    const columnSamples = get(validationResult, "columnSamples", {});
    const FIELD_LABELS = PARAMS_IMPORT.MAPPING.FIELDS;

    const usedColumns = new Set(Object.values(mapping).filter(Boolean));
    const requiredFields = fields.filter((f) => f.required);
    const mappedRequired = requiredFields.filter((f) => mapping[f.key]).length;

    const handleFieldChange = (fieldKey, columnName) => {
      setMapping({
        ...mapping,
        [fieldKey]: columnName || undefined,
      });
    };

    const handleClear = (fieldKey) => {
      const next = { ...mapping };
      delete next[fieldKey];
      setMapping(next);
    };

    const columnOptions = headers.map((h) => ({
      label: usedColumns.has(h) ? `${h} (${PARAMS_IMPORT.MAPPING.USED})` : h,
      value: h,
      disabled:
        usedColumns.has(h) && !Object.entries(mapping).some(([, v]) => v === h),
    }));

    const confirmButton = (
      <Button
        type="primary"
        onClick={() => handleMappingConfirm(mapping)}
        disabled={mappedRequired < requiredFields.length || validating}
        loading={validating}
      >
        <BiCheck size={20} />
        {PARAMS_IMPORT.MAPPING.CONFIRM}
      </Button>
    );

    return (
      <StepCard
        title={PARAMS_IMPORT.MAPPING.TITLE}
        description={PARAMS_IMPORT.MAPPING.DESCRIPTION}
        onBack={goBack}
        backLabel={PARAMS_IMPORT.BACK_CHANGE_FILE}
        actions={confirmButton}
      >
        <div className="pricers-import__progress-bar">
          <span className="pricers-import__progress-label">
            {mappedRequired}/{requiredFields.length}{" "}
            {PARAMS_IMPORT.MAPPING.REQUIRED_MAPPED}
          </span>
          <div className="pricers-import__progress-track">
            <div
              className={classNames("pricers-import__progress-fill", {
                "pricers-import__progress-fill--complete":
                  mappedRequired === requiredFields.length,
              })}
              style={{
                width: `${(mappedRequired / requiredFields.length) * 100}%`,
              }}
            />
          </div>
        </div>

        <table className="pricers-table">
          <thead>
            <tr className="pricers-table__header-row">
              <th className="pricers-table__th">
                {PARAMS_IMPORT.MAPPING.EXPECTED_FIELD}
              </th>
              <th className="pricers-table__th">
                {PARAMS_IMPORT.MAPPING.FILE_COLUMN}
              </th>
              <th className="pricers-table__th">
                {PARAMS_IMPORT.MAPPING.PREVIEW}
              </th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => {
              const translationKey = field.key.toUpperCase();
              const isMapped = !!mapping[field.key];
              const isRequired = field.required;

              return (
                <tr key={field.key} className="pricers-table__row">
                  <td className="pricers-table__td">
                    <div className="d-flex align--center gap-8">
                      <span
                        className={classNames("pricers-import__status-dot", {
                          "pricers-import__status-dot--mapped": isMapped,
                          "pricers-import__status-dot--required":
                            !isMapped && isRequired,
                          "pricers-import__status-dot--optional":
                            !isMapped && !isRequired,
                        })}
                      />
                      <span className="pricers-import__field-label">
                        {FIELD_LABELS[translationKey] || field.key}
                      </span>
                      {isRequired && (
                        <span className="pricers-import__required-badge">
                          {PARAMS_IMPORT.MAPPING.REQUIRED}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="pricers-table__td">
                    <div className="d-flex align--center gap-8">
                      <Select
                        showSearch
                        options={columnOptions}
                        value={mapping[field.key] || undefined}
                        placeholder={PARAMS_IMPORT.MAPPING.SELECT_COLUMN}
                        onChange={(val) => handleFieldChange(field.key, val)}
                        allowClear
                        className="pricers-import__mapping-select"
                      />
                      {isMapped && (
                        <MdClose
                          size={18}
                          color={COLORS.C_GRAY}
                          className="pricers-import__clickable"
                          onClick={() => handleClear(field.key)}
                        />
                      )}
                    </div>
                  </td>
                  <td className="pricers-table__td">
                    <div className="d-flex gap-4 flex--wrap">
                      {(columnSamples[mapping[field.key]] || [])
                        .slice(0, 4)
                        .map((val, i) => (
                          <span key={i} className="pricers-import__sample-tag">
                            {val}
                          </span>
                        ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </StepCard>
    );
  };

  const renderValidation = () => {
    const isValid = get(validationResult, "valid", false);
    const errors = get(validationResult, "errors", []);
    const warnings = get(validationResult, "warnings", []);
    const preview = get(validationResult, "preview", {});
    const totalRows = get(validationResult, "totalRows", 0);
    const plans = get(preview, "plans", []);

    let totalParamSets = 0;
    let totalRules = 0;
    for (const plan of plans) {
      totalParamSets += plan.paramSetCount || 0;
      totalRules += plan.ruleCount || 0;
    }

    const nextButton = (
      <Button type="primary" onClick={handleValidationNext} disabled={!isValid}>
        <BiCheck size={20} />
        {PARAMS_IMPORT.VALIDATION.NEXT}
      </Button>
    );

    return (
      <StepCard
        title={PARAMS_IMPORT.VALIDATION.TITLE}
        onBack={goBack}
        backLabel={PARAMS_IMPORT.BACK_EDIT_MAPPING}
        actions={nextButton}
      >
        <div
          className={classNames("pricers-import__status-banner", {
            "pricers-import__status-banner--success": isValid,
            "pricers-import__status-banner--error": !isValid,
          })}
        >
          {isValid ? (
            <MdOutlineCheckCircle size={24} color={COLORS.C_SUCCESS} />
          ) : (
            <MdErrorOutline size={24} color={COLORS.C_DANGER} />
          )}
          <div>
            <strong>
              {isValid
                ? PARAMS_IMPORT.VALIDATION.SUCCESS
                : `${errors.length} ${PARAMS_IMPORT.VALIDATION.ERRORS_FOUND}`}
            </strong>
            <p className="pricers-import__status-meta">
              {totalRows} {PARAMS_IMPORT.VALIDATION.ROWS_ANALYZED}
            </p>
          </div>
        </div>

        {errors.length > 0 && (
          <div className="pricers-import__error-section">
            <h4 className="pricers-import__error-title">
              {PARAMS_IMPORT.VALIDATION.ERROR_DETAILS}
            </h4>
            <div className="pricers-import__error-list">
              {errors.slice(0, 30).map((err, i) => (
                <div key={i} className="pricers-import__error-item">
                  <MdErrorOutline size={16} />
                  {resolveError(err, PARAMS_IMPORT.ERROR_KEYS)}
                </div>
              ))}
            </div>
          </div>
        )}

        {warnings.length > 0 && (
          <div className="mb-24">
            {warnings.map((warn, i) => (
              <div key={i} className="pricers-import__warning-item">
                {typeof warn === "string" ? warn : JSON.stringify(warn)}
              </div>
            ))}
          </div>
        )}

        <div className="grid--3 gap-16 mb-24">
          <div className="pricers-import__stat-card">
            <MdOutlineCategory
              size={22}
              className="pricers-import__stat-icon"
            />
            <div className="pricers-import__stat-value">{plans.length}</div>
            <div className="pricers-import__stat-label">
              {PARAMS_IMPORT.VALIDATION.PLANS_DETECTED}
            </div>
          </div>
          <div className="pricers-import__stat-card">
            <MdOutlineTableRows
              size={22}
              className="pricers-import__stat-icon"
            />
            <div className="pricers-import__stat-value">{totalParamSets}</div>
            <div className="pricers-import__stat-label">
              {PARAMS_IMPORT.VALIDATION.PARAM_SETS}
            </div>
          </div>
          <div className="pricers-import__stat-card">
            <MdOutlineCategory
              size={22}
              className="pricers-import__stat-icon"
            />
            <div className="pricers-import__stat-value">{totalRules}</div>
            <div className="pricers-import__stat-label">
              {PARAMS_IMPORT.VALIDATION.RULES}
            </div>
          </div>
        </div>

        {plans.length > 0 && (
          <div className="pricers-import__product-list">
            <h4 className="m-bottom-12">
              {PARAMS_IMPORT.VALIDATION.PLAN_TABLE}
            </h4>
            <div className="pricers-import__product-grid">
              {plans.slice(0, 50).map((p, i) => (
                <div key={i} className="pricers-import__product-item">
                  <div className="pricers-import__product-name">
                    {p.identifier}
                  </div>
                  <div className="pricers-import__product-meta">
                    <span className="pricers-import__product-tag">
                      {p.paramSetCount} {PARAMS_IMPORT.VALIDATION.PARAM_SETS}
                    </span>
                    <span className="pricers-import__product-rows">
                      {p.ruleCount} {PARAMS_IMPORT.VALIDATION.RULES}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </StepCard>
    );
  };

  const renderAnalysis = () => {
    const preview = get(validationResult, "preview", {});
    const plans = get(preview, "plans", []);
    const existingPlans = get(preview, "existingPlans", 0);
    const notFoundPlans = get(preview, "notFoundPlans", 0);
    const totalRows = get(validationResult, "totalRows", 0);

    const launchButton = (
      <Button
        type="primary"
        onClick={handleExecuteImport}
        disabled={!confirmed}
      >
        <BiCheck size={20} />
        {PARAMS_IMPORT.ANALYSIS.LAUNCH_IMPORT}
      </Button>
    );

    return (
      <StepCard
        title={PARAMS_IMPORT.ANALYSIS.TITLE}
        description={PARAMS_IMPORT.ANALYSIS.DESCRIPTION}
        onBack={goBack}
        backLabel={PARAMS_IMPORT.BACK_REVIEW_VALIDATION}
        actions={launchButton}
      >
        <div className="grid--3 gap-16 mb-24">
          <div className="pricers-import__analysis-stat pricers-import__analysis-stat--total">
            <div className="pricers-import__analysis-value pricers-import__analysis-value--total">
              {totalRows}
            </div>
            <div className="pricers-import__analysis-label pricers-import__analysis-label--muted">
              {PARAMS_IMPORT.ANALYSIS.TOTAL_ROWS}
            </div>
          </div>
          <div className="pricers-import__analysis-stat pricers-import__analysis-stat--existing">
            <div className="d-flex align--center justify--center gap-6">
              <MdOutlineCheckCircle size={22} color={COLORS.C_SUCCESS} />
              <span className="pricers-import__analysis-value pricers-import__analysis-value--existing">
                {existingPlans}
              </span>
            </div>
            <div className="pricers-import__analysis-label pricers-import__analysis-label--existing">
              {PARAMS_IMPORT.ANALYSIS.PLANS_FOUND}
            </div>
            <div className="pricers-import__analysis-sublabel">
              {PARAMS_IMPORT.ANALYSIS.PLANS_FOUND_DESC}
            </div>
          </div>
          <div className="pricers-import__analysis-stat pricers-import__analysis-stat--new">
            {notFoundPlans > 0 ? (
              <div className="d-flex align--center justify--center gap-6">
                <MdOutlineWarning size={22} color={COLORS.C_DANGER} />
                <span className="pricers-import__analysis-value pricers-import__analysis-value--new">
                  {notFoundPlans}
                </span>
              </div>
            ) : (
              <div className="pricers-import__analysis-value pricers-import__analysis-value--new">
                {notFoundPlans}
              </div>
            )}
            <div className="pricers-import__analysis-label pricers-import__analysis-label--new">
              {PARAMS_IMPORT.ANALYSIS.PLANS_NOT_FOUND}
            </div>
            <div className="pricers-import__analysis-sublabel">
              {PARAMS_IMPORT.ANALYSIS.PLANS_NOT_FOUND_DESC}
            </div>
          </div>
        </div>

        <div className="pricers-import__confirm-banner pricers-import__confirm-banner--warning">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="pricers-checkbox pricers-checkbox--lg"
          />
          <span className="pricers-import__confirm-label">
            {PARAMS_IMPORT.ANALYSIS.CONFIRM_ALL}
          </span>
        </div>
      </StepCard>
    );
  };

  const renderImporting = () => (
    <StepCard>
      <div className="pricers-import__loading-container">
        <div className="pricers-import__loading-icon">
          <Spin size="large" />
        </div>
        <h2>{PARAMS_IMPORT.IMPORTING.TITLE}</h2>
        <p className="pricers-import__loading-description">
          {PARAMS_IMPORT.IMPORTING.DESCRIPTION}
        </p>
        <p className="pricers-import__loading-warning">
          {PARAMS_IMPORT.IMPORTING.WARNING}
        </p>
      </div>
    </StepCard>
  );

  const renderResult = () => {
    const result = importResult || {
      success: false,
      errors: [{ key: "UNEXPECTED_ERROR", params: { message: "" } }],
    };
    const success = get(result, "success", false);
    const errors = get(result, "errors", []);
    const updated = get(result, "updated", 0);
    const skipped = get(result, "skipped", 0);
    const totalRows = get(result, "totalRows", 0);

    const resultActions = (
      <>
        <Button type="primary--outlined" onClick={handleReset}>
          <MdOutlineRefresh size={20} />
          {PARAMS_IMPORT.RESULT.IMPORT_ANOTHER}
        </Button>
        <Button type="primary" onClick={handleFinish}>
          <MdOutlineExplore size={20} />
          {PARAMS_IMPORT.RESULT.VIEW_PLANS}
        </Button>
      </>
    );

    return (
      <StepCard actions={resultActions}>
        <div className="pricers-import__result-container">
          <div
            className={classNames("pricers-import__result-icon", {
              "pricers-import__result-icon--success": success,
              "pricers-import__result-icon--failure": !success,
            })}
          >
            {success ? (
              <MdOutlineCheckCircle size={48} color={COLORS.C_SUCCESS} />
            ) : (
              <MdOutlineError size={48} color={COLORS.C_DANGER} />
            )}
          </div>
          <h2>
            {success
              ? PARAMS_IMPORT.RESULT.SUCCESS_TITLE
              : PARAMS_IMPORT.RESULT.FAILURE_TITLE}
          </h2>
        </div>

        {!success && errors.length > 0 && (
          <div className="pricers-import__error-section">
            <h4 className="pricers-import__error-title">
              {PARAMS_IMPORT.RESULT.ERROR_DETAILS}
            </h4>
            <div className="pricers-import__error-list">
              {errors.map((err, i) => (
                <div key={i} className="pricers-import__error-item">
                  {resolveError(err, PARAMS_IMPORT.ERROR_KEYS)}
                </div>
              ))}
            </div>
          </div>
        )}

        {success && (
          <>
            <div className="grid--2 gap-16 mb-16">
              <div className="pricers-import__result-stat pricers-import__result-stat--updated">
                <div className="pricers-import__result-stat-value pricers-import__result-stat-value--updated">
                  {updated}
                </div>
                <div className="pricers-import__stat-label">
                  {PARAMS_IMPORT.RESULT.UPDATED}
                </div>
              </div>
              <div className="pricers-import__result-stat pricers-import__result-stat--skipped">
                <div className="pricers-import__result-stat-value pricers-import__result-stat-value--skipped">
                  {skipped}
                </div>
                <div className="pricers-import__stat-label">
                  {PARAMS_IMPORT.RESULT.SKIPPED}
                </div>
              </div>
            </div>

            <div className="pricers-import__result-footer">
              <span>
                {totalRows} {PARAMS_IMPORT.RESULT.ROWS_PROCESSED}
              </span>
            </div>
          </>
        )}
      </StepCard>
    );
  };

  return (
    <>
      <Header
        title={PARAMS_IMPORT.TITLE}
        subtitle={PARAMS_IMPORT.SUBTITLE}
        actions={
          stepName === PARAMS_IMPORT_STEPS.UPLOAD
            ? [
                {
                  text: PARAMS_IMPORT.BACK,
                  onClick: goBack,
                  type: "primary--outlined",
                  icon: <BiChevronLeft size={20} />,
                },
              ]
            : []
        }
      />
      <div className="pricers-import__stepper">
        {stepLabels.map((label, idx) => {
          const isActive = idx === visibleStepIndex;
          const isDone = idx < visibleStepIndex;
          return (
            <div key={idx} className="pricers-import__step-item">
              <div className="pricers-import__step-row">
                <div
                  className={classNames("pricers-import__step-connector", {
                    "pricers-import__step-connector--done": isDone || isActive,
                    "pricers-import__step-connector--hidden": idx === 0,
                  })}
                />
                <span
                  className={classNames("pricers-import__step-number", {
                    "pricers-import__step-number--active": isActive,
                    "pricers-import__step-number--done": isDone,
                  })}
                >
                  {isDone ? "\u2713" : idx + 1}
                </span>
                <div
                  className={classNames("pricers-import__step-connector", {
                    "pricers-import__step-connector--done": isDone,
                    "pricers-import__step-connector--hidden":
                      idx === stepLabels.length - 1,
                  })}
                />
              </div>
              <span
                className={classNames("pricers-import__step-label", {
                  "pricers-import__step-label--active": isActive,
                  "pricers-import__step-label--done": isDone,
                })}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>

      {stepName === PARAMS_IMPORT_STEPS.UPLOAD && renderUpload()}
      {stepName === PARAMS_IMPORT_STEPS.MAPPING &&
        validationResult &&
        renderMapping()}
      {stepName === PARAMS_IMPORT_STEPS.VALIDATION &&
        validationResult &&
        renderValidation()}
      {stepName === PARAMS_IMPORT_STEPS.VALUE_MAPPING && validationResult && (
        <StepValueMapping
          sections={buildParamsSections()}
          onConfirm={handleValueMappingConfirm}
          persistedState={valueMappingState}
          onStateChange={setValueMappingState}
          onBack={goBack}
          backLabel={PARAMS_IMPORT.BACK_REVIEW_VALIDATION}
          labels={PARAMS_IMPORT.VALUE_MAPPING || {}}
        />
      )}
      {stepName === PARAMS_IMPORT_STEPS.ANALYSIS &&
        validationResult &&
        renderAnalysis()}
      {stepName === PARAMS_IMPORT_STEPS.IMPORTING && renderImporting()}
      {stepName === PARAMS_IMPORT_STEPS.RESULT && renderResult()}
    </>
  );
};

export default ParamsImportWizard;
